import React from 'react';
import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { LegalPageLayout, Section, Callout, Bullets } from '@/components/LegalPageLayout';

const UPDATED = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const PrivacyPolicy = () => (
  <LegalPageLayout
    title="Privacy Policy"
    lastUpdated={UPDATED}
    icon={Shield}
    testid="privacy-page"
    intro={
      <p>
        This Privacy Policy explains what information Option Wizard (&ldquo;we&rdquo;, &ldquo;us&rdquo;) collects,
        why we collect it, how it is used, and the third-party service providers it is shared with. It reflects the
        platform&rsquo;s actual implementation. Please also review our{' '}
        <Link to="/terms" className="text-amber-300 hover:underline underline-offset-4">Terms of Service</Link>.
      </p>
    }
  >
    <Callout tone="blue" title="Plain-language summary">
      We collect the information needed to run your account, subscription and the analysis tools you use. To provide
      the service we send certain data to trusted providers — an authentication provider, a payment processor, AI
      providers, market-data sources, and any brokerage you choose to connect. We do <strong>not</strong> sell your
      personal information.
    </Callout>

    <Section title="1. Information You Provide">
      <Bullets items={[
        'Account details: your name and email address.',
        'Authentication: if you sign in with Google, we receive your basic Google profile (name, email, profile picture) via our authentication provider. If you register with email/password, your password is stored only as a secure one-way hash (bcrypt) — we never store it in plain text.',
        'Content you create: watchlists, saved strategy analyses, roll and scanner analyses, portfolio positions you enter or import, and messages you submit through the contact form.',
        'Brokerage connections (optional): if you connect a brokerage (e.g., Tastytrade, Schwab, Public) or import a CSV, we process the position/account data required for the feature. Access tokens are stored encrypted.',
      ]} />
    </Section>

    <Section title="2. Information Collected Automatically">
      <Bullets items={[
        'Session data: a session token (stored in a cookie and in our database) to keep you signed in.',
        'Operational logs and basic request telemetry (including AI request telemetry) used to run, secure and debug the service.',
        'Approximate IP address and user-agent when you submit the contact form, used for spam prevention and abuse protection.',
        'Local storage in your browser (e.g., your acknowledgement of the legal disclaimer).',
      ]} />
    </Section>

    <Section title="3. How We Use Information">
      <Bullets items={[
        'To create and maintain your account and keep you signed in.',
        'To provide the analysis, scanner, portfolio and AI-assisted research features you request.',
        'To process subscriptions, trials, renewals and billing through our payment processor.',
        'To operate, secure, troubleshoot and improve the platform.',
        'To respond to contact-form messages you send us.',
      ]} />
    </Section>

    <Section title="4. Service Providers We Share Data With">
      <p>
        To operate the platform we transmit certain information to the following categories of providers. We share only
        what is necessary for each function:
      </p>
      <Bullets items={[
        'Authentication provider — to verify Google sign-in sessions.',
        'Payment processor (Stripe) — to create customers, process subscription payments, trials and renewals, and manage the billing portal. Card details are handled by Stripe, not stored on our servers.',
        'AI providers (e.g., OpenAI, Anthropic, Google) — the inputs required to generate analysis (such as the tickers, positions or questions involved in a request) are sent to the selected AI provider to produce a response.',
        'Market-data sources — used to retrieve quotes, options and related market information.',
        'Brokerages you choose to connect — only when you initiate and authorize a connection or import.',
        'Infrastructure/hosting providers — to host and run the application.',
      ]} />
      <Callout tone="amber" title="We do not claim data is never shared">
        Because the service depends on the providers above, some data is necessarily transmitted to them. We do not sell
        your personal information, and we do not share it for third-party advertising.
      </Callout>
    </Section>

    <Section title="5. Data Security">
      <p>
        We use reasonable technical measures appropriate to the service, including hashed passwords (bcrypt), encrypted
        storage of brokerage access tokens, and session-based authentication. No method of transmission or storage is
        completely secure, and we cannot guarantee absolute security. We do not claim end-to-end encryption of all data.
      </p>
    </Section>

    <Section title="6. Data Retention & Deletion">
      <p>The following reflects the platform&rsquo;s current, verified capabilities:</p>
      <Bullets items={[
        'Account & content: retained while your account is active.',
        'Brokerage connections: you can disconnect a connected brokerage at any time. Disconnecting deletes the stored access tokens for that brokerage from our database (verified for the supported brokerage integrations).',
        'Subscriptions: you can cancel at any time via the Stripe billing portal. Billing and transaction records are retained as needed for accounting, tax and legal-compliance purposes.',
        'AI requests: content you submit for AI analysis is transmitted to third-party AI providers; retention of that data is governed by those providers\u2019 own policies and is not controlled by us.',
      ]} />
      <Callout tone="amber" title="Account Deletion">
        You can delete your account yourself at any time from the account menu (<em>Delete Account</em>). Deletion
        signs you out everywhere, removes stored brokerage credentials and synced portfolio data, and removes your
        name and email from your profile. Any active subscription is cancelled at the end of its current billing
        period. We do not claim that all copies are immediately erased from backups. Certain records (such as
        billing/transaction records) are retained where required by law. You may also request deletion through the{' '}
        <Link to="/contact" className="underline underline-offset-4">contact form</Link>.
      </Callout>
    </Section>

    <Section title="7. Your Choices & Requests">
      <Bullets items={[
        'You can update your profile information from your account.',
        'You can cancel your subscription at any time through the Stripe billing portal.',
        'You can disconnect a brokerage integration you previously connected.',
        'You can request access to or deletion of your personal data by contacting us through the contact form.',
      ]} />
      <p>
        Depending on where you live, you may have additional rights under applicable privacy laws. To exercise any such
        rights, contact us through the contact form.
      </p>
    </Section>

    <Section title="8. Cookies & Local Storage">
      <p>
        We use a session cookie to keep you authenticated and browser local storage for preferences such as your
        disclaimer acknowledgement. We do not use third-party advertising cookies.
      </p>
    </Section>

    <Section title="9. Children's Privacy">
      <p>
        The platform is intended for adults and is not directed to children. We do not knowingly collect personal
        information from children.
      </p>
    </Section>

    <Section title="10. International Users">
      <p>
        If you access the platform from outside the provider&rsquo;s hosting region, your information may be processed in
        other jurisdictions, where data-protection laws may differ from those in your home jurisdiction.
      </p>
    </Section>

    <Section title="11. Changes to This Policy">
      <p>We may update this Policy from time to time. Material changes will be reflected by updating the &ldquo;Last Updated&rdquo; date above.</p>
    </Section>

    <Section title="12. Contact">
      <p>
        Questions or privacy requests? Use our{' '}
        <Link to="/contact" className="text-amber-300 hover:underline underline-offset-4">contact form</Link>. This
        service is operated by Option Wizard. Our mailing address is:
      </p>
      <address className="not-italic rounded-lg border border-slate-700 bg-slate-800/50 p-4 text-slate-200 leading-relaxed" data-testid="privacy-business-address">
        Timothy Woods<br />Option Wizard<br />200 N. Vineyard Blvd<br />Ste A325-186<br />Honolulu, Hawaii 96817
      </address>
    </Section>
  </LegalPageLayout>
);

export default PrivacyPolicy;
